/**
 * Section-HTML text-walking and text-wrapping primitives.
 *
 * Shared by:
 *   - client-info-migrate.js   (Resync from Client Info)
 *   - section-text-search.js   (Find & Replace search pass)
 *   - section-text-replace.js  (Find & Replace mutation pass)
 *
 * Walker rules:
 *   - Skips text inside <script> and <style>
 *   - By default skips text inside elements with data-pb-client or
 *     data-pb-client-text (idempotency — re-running migration produces no
 *     further matches inside those spans). Pass { includeBoundText: true } for
 *     read-only search surfaces that need to display existing bindings.
 *   - By default skips text inside form descendants (FORM/INPUT/TEXTAREA/
 *     SELECT/OPTION/LABEL/BUTTON). Pass { includeFormText: true } to descend
 *     into LABEL/BUTTON text — used by Find & Replace whose scope deliberately
 *     extends to those user-visible strings.
 *
 * Wrapping rules:
 *   - wrapTextMatch / wrapBoundedTextMatch replace the matched slice of a
 *     text node with <span data-pb-client="bindingKey">displayValue</span>.
 *     When displayValue is omitted, the span content equals the matched
 *     text (pure bind). When displayValue is provided, the span content
 *     is the new value (rename + bind — the migrator's source-profile path).
 */

const FORM_ANCESTORS = new Set([
  'FORM',
  'INPUT',
  'TEXTAREA',
  'SELECT',
  'OPTION',
  'LABEL',
  'BUTTON'
]);

export function isInForm(node) {
  let cur = node.parentNode;
  while (cur && cur.nodeType === 1) {
    if (FORM_ANCESTORS.has(cur.tagName)) return true;
    cur = cur.parentNode;
  }
  return false;
}

export function escapeRegExp(value) {
  return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Walk every text node under `root` and call `visitor(node)` on each.
 * Returns no value — callers may mutate the DOM during the walk.
 *
 * options.includeFormText (default false): when true, allow descent into
 * LABEL/BUTTON text but still skip FORM/INPUT/TEXTAREA/SELECT/OPTION (input
 * values and select children are never user-visible "text content" worth
 * matching). When false, the legacy migrator behavior — skip ALL form
 * descendants.
 *
 * options.includeBoundText (default false): when true, include text inside
 * data-pb-client / data-pb-client-text spans. Find & Replace uses this to list
 * already-bound text as read-only hits; the migrator keeps the default skip.
 */
export function walkTextNodes(root, visitor, options = {}) {
  const { includeFormText = false, includeBoundText = false } = options;
  const doc = root.ownerDocument;
  const walker = doc.createTreeWalker(root, 4 /* SHOW_TEXT */);
  const nodes = [];
  let node;
  while ((node = walker.nextNode())) nodes.push(node);
  for (const n of nodes) {
    if (!n.parentNode) continue;
    if (!includeFormText && isInForm(n)) continue;
    if (includeFormText && isInRestrictedFormControl(n)) continue;
    // Skip text inside already-bound elements + script/style.
    let cur = n.parentNode;
    let skip = false;
    while (cur && cur.nodeType === 1) {
      if (
        !includeBoundText &&
        cur.hasAttribute &&
        (cur.hasAttribute('data-pb-client') || cur.hasAttribute('data-pb-client-text'))
      ) {
        skip = true;
        break;
      }
      const tag = cur.tagName;
      if (tag === 'SCRIPT' || tag === 'STYLE') {
        skip = true;
        break;
      }
      cur = cur.parentNode;
    }
    if (skip) continue;
    visitor(n);
  }
}

// LABEL and BUTTON are user-visible text inside a <form> — Find & Replace
// wants in. The rest of FORM_ANCESTORS (INPUT/TEXTAREA/SELECT/OPTION) host
// input values / option lists that aren't rendered as regular text content.
// FORM itself is just a container — text directly inside it (if any) is OK.
const RESTRICTED_FORM_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT', 'OPTION']);
function isInRestrictedFormControl(node) {
  let cur = node.parentNode;
  while (cur && cur.nodeType === 1) {
    if (RESTRICTED_FORM_TAGS.has(cur.tagName)) return true;
    cur = cur.parentNode;
  }
  return false;
}

/**
 * Wrap the first occurrence of `needle` inside `textNode` with
 * <span data-pb-client="bindingKey">displayValue</span>. Leaves the rest of
 * the text node untouched. Returns true if a substitution occurred.
 *
 * `displayValue` defaults to `needle` (pure bind). When a rename is desired
 * (needle = old value, displayValue = new value), the span's inner text is
 * the new value while the surrounding text node is left intact.
 */
export function wrapTextMatch(textNode, needle, bindingKey, displayValue) {
  if (!needle) return false;
  const text = textNode.nodeValue;
  const idx = text.indexOf(needle);
  if (idx === -1) return false;
  return wrapTextSlice(
    textNode,
    idx,
    needle.length,
    bindingKey,
    displayValue != null ? displayValue : needle
  );
}

/**
 * Word-bounded wrapper: matches needle only when surrounded by non-alphanumeric
 * characters (or string boundaries). Used by migrator's address-parts / name /
 * tagline rules so e.g. "Acme" doesn't bind inside "Bladeworks Acmebrothers".
 */
export function wrapBoundedTextMatch(textNode, needle, bindingKey, displayValue) {
  if (!needle) return false;
  const text = textNode.nodeValue;
  const re = new RegExp(`(^|[^A-Za-z0-9])(${escapeRegExp(needle)})(?=$|[^A-Za-z0-9])`);
  const match = re.exec(text);
  if (!match) return false;
  const leading = match[1] || '';
  const start = match.index + leading.length;
  const matchLen = match[2].length;
  const matched = text.slice(start, start + matchLen);
  return wrapTextSlice(
    textNode,
    start,
    matchLen,
    bindingKey,
    displayValue != null ? displayValue : matched
  );
}

/**
 * Lower-level wrapper used by Find & Replace's replace pass which already
 * has exact start/length coordinates from the search results. Replaces
 * text[start..start+length] with <span data-pb-client="bindingKey">value</span>
 * (when bindingKey is non-null) or just a plain text replacement (when null).
 * Returns true on success, false on out-of-range.
 */
export function wrapTextSlice(textNode, start, length, bindingKey, replacement) {
  const text = textNode.nodeValue;
  if (start < 0 || start + length > text.length) return false;
  const doc = textNode.ownerDocument;
  const before = text.slice(0, start);
  const after = text.slice(start + length);
  const frag = doc.createDocumentFragment();
  if (before) frag.appendChild(doc.createTextNode(before));
  if (bindingKey) {
    const span = doc.createElement('span');
    span.setAttribute('data-pb-client', bindingKey);
    span.textContent = replacement;
    frag.appendChild(span);
  } else if (replacement) {
    frag.appendChild(doc.createTextNode(replacement));
  }
  if (after) frag.appendChild(doc.createTextNode(after));
  textNode.parentNode.replaceChild(frag, textNode);
  return true;
}
